
import { Permission } from "../types";
import { defaultPermissions, PERMISSIONS_STORAGE_KEY } from "../defaultPermissions";
import { deepClone } from "../utils/helpers";
import { isLocalStorageAvailable } from "../utils/helpers";
import { savePermissionsToStorage, debugLogStorageContent } from "../storage/localStorageManager";
import { 
  savePermissionsToDatabase, 
  initializeDefaultPermissions, 
  syncLocalToDatabase 
} from "../storage/databaseManager";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Load permissions from database with localStorage cache fallback
 */
export const loadPermissions = async (): Promise<Permission[]> => {
  try {
    console.log("[PermissionsStorage] Starting to load permissions with database priority");
    
    // Try to load from database first - this is now our source of truth
    try {
      console.log("[PermissionsStorage] Attempting to fetch permissions from database");
      
      const { data: dbPermissions, error } = await supabase
        .from('role_permissions')
        .select('*');
      
      if (error) {
        console.error("[PermissionsStorage] Error fetching from database:", error);
        toast.error("Fout bij ophalen van rechten uit database");
        throw error;
      }
      
      if (dbPermissions && dbPermissions.length > 0) {
        console.log("[PermissionsStorage] Successfully loaded permissions from database:", dbPermissions.length);
        toast.success(`Rechten geladen uit database (${dbPermissions.length} items)`);
        
        // Save to localStorage as cache
        savePermissionsToStorage(dbPermissions);
        
        return deepClone(dbPermissions);
      } else {
        console.log("[PermissionsStorage] No permissions found in database, will initialize with defaults");
        toast.info("Geen rechten gevonden in database, standaardrechten worden gebruikt");
        
        // Initialize database with defaults if empty
        const defaults = deepClone(defaultPermissions);
        const initialized = await initializeDefaultPermissions(defaults);
        
        if (initialized) {
          toast.success("Standaardrechten succesvol geïnitialiseerd in database");
        }
        
        // Immediately reload from database to get the newly created records with proper UUIDs
        try {
          const { data: freshPermissions, error: reloadError } = await supabase
            .from('role_permissions')
            .select('*');
            
          if (!reloadError && freshPermissions && freshPermissions.length > 0) {
            console.log("[PermissionsStorage] Successfully loaded newly initialized permissions:", freshPermissions.length);
            return deepClone(freshPermissions);
          }
        } catch (reloadError) {
          console.error("[PermissionsStorage] Failed to reload after initialization:", reloadError);
        }
        
        return defaults;
      }
    } catch (dbError) {
      console.error("[PermissionsStorage] Database fetch failed, falling back to localStorage:", dbError);
      toast.error("Fout bij verbinding met database, lokale cache wordt gebruikt");
      
      // Try to load from localStorage as fallback
      try {
        const savedPermissionsJson = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
        
        if (savedPermissionsJson) {
          console.log("[PermissionsStorage] Using localStorage cache as fallback");
          const parsedPermissions = JSON.parse(savedPermissionsJson);
          
          if (Array.isArray(parsedPermissions) && parsedPermissions.length > 0) {
            console.log("[PermissionsStorage] Using cached permissions from localStorage:", parsedPermissions.length);
            toast.info("Rechten geladen uit lokale cache");
            
            // Try to sync back to database when it becomes available
            syncLocalToDatabase(parsedPermissions).catch(e => 
              console.warn("[PermissionsStorage] Failed to sync local cache to database:", e)
            );
            
            return deepClone(parsedPermissions);
          }
        }
      } catch (localError) {
        console.error("[PermissionsStorage] Error accessing localStorage:", localError);
      }
      
      // If all else fails, use defaults
      console.log("[PermissionsStorage] No valid cache found, using default permissions");
      toast.info("Geen geldige cache gevonden, standaardrechten worden gebruikt");
      const defaults = deepClone(defaultPermissions);
      
      // Try to initialize database with defaults (async, may fail)
      initializeDefaultPermissions(defaults).catch(e => 
        console.warn("[PermissionsStorage] Failed to initialize database with defaults:", e)
      );
      
      return defaults;
    }
  } catch (error) {
    console.error("[PermissionsStorage] Critical error loading permissions:", error);
    toast.error("Kritieke fout bij laden van rechten, standaardwaarden worden gebruikt");
    return deepClone(defaultPermissions);
  }
};
